import { createApi } from '@reduxjs/toolkit/dist/query/react';
import B2ChatClient, { apiResource, b2chatBaseQuery } from '@src/client';
import {
  ContactTag,
  ConversationTag,
} from '@src/components/AdminHome/Tags/types';

export type SearchTagsResponse<T> = {
  total: number;
  list: T[];
  pageNum: number;
  pageSize: number;
  size: number;
  startRow: number;
  endRow: number;
  pages: number;
  prePage: number;
  nextPage: number;
  isFirstPage: boolean;
  isLastPage: boolean;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  navigatePages: number;
  navigatepageNums: number[];
  navigateFirstPage: number;
  navigateLastPage: number;
};

export type AssignedConversationTagsRequest = {
  chatId: string;
};
export type SearchConversationTagsRequest = {
  botId: string | number;
  pageNum: number;
  pageSize: number;
  filter: {
    searchFilter: string;
    stateFilter?: null | string;
  };
  sorting?: {
    column: 'tag_name';
    direction: 'ASC';
  }[];
};
export type SearchConversationTagsResponse =
  SearchTagsResponse<ConversationTag>;

export type SearchContactTagsRequest = {
  pageNum: number;
  pageSize: number;
  filter: {
    searchFilter: string;
  };
};
export type SearchContactTagsResponse = SearchTagsResponse<ContactTag>;

const mergeData = <T>(
  currentCacheData: SearchTagsResponse<T>,
  responseData: SearchTagsResponse<T>
) => {
  if (!currentCacheData) {
    return responseData;
  }
  const list = currentCacheData.list.slice();
  list.splice(
    (responseData.pageNum - 1) * responseData.pageSize,
    responseData.pageSize,
    ...responseData.list
  );
  return {
    ...currentCacheData,
    ...responseData,
    list,
  };
};

export const TagsApi = createApi({
  reducerPath: 'tagsApi',
  baseQuery: b2chatBaseQuery,
  tagTypes: ['tags/conversation', 'tags/conversation/assigned', 'tags/contact'],
  endpoints: builder => ({
    searchConversationTags: builder.query<
      SearchConversationTagsResponse,
      SearchConversationTagsRequest
    >({
      providesTags: ['tags/conversation'],
      serializeQueryArgs: ({ endpointName, queryArgs }) => ({
        endpointName,
        botId: queryArgs.botId,
        queryArgs: queryArgs.filter?.searchFilter || '',
      }),
      keepUnusedDataFor: 0,
      merge: mergeData,
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.pageNum !== previousArg?.pageNum;
      },
      query: request =>
        apiResource(B2ChatClient.resources.newTagsAdmin.actions.search, {
          params: {
            botId: `${request.botId}`,
            pageNum: `${request.pageNum}`,
            pageSize: `${request.pageSize}`,
          },
          data: {
            filter: {
              searchFilter: request.filter.searchFilter,
              stateFilter: request.filter.stateFilter || null,
            },
            sorting: request.sorting || [
              {
                column: 'tag_name',
                direction: 'ASC',
              },
            ],
          },
        }),
      extraOptions: {
        maxRetries: 1,
      },
    }),
    searchContactTags: builder.query<
      SearchContactTagsResponse,
      SearchContactTagsRequest
    >({
      serializeQueryArgs: ({ endpointName, queryArgs }) => ({
        endpointName,
        queryArgs: queryArgs.filter?.searchFilter || '',
      }),
      providesTags: ['tags/contact'],
      keepUnusedDataFor: 0,
      merge: mergeData,
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.pageNum !== previousArg?.pageNum;
      },
      query: request =>
        apiResource(B2ChatClient.resources.newTags.actions.getTags, {
          params: {
            type: 'CONTACT',
            pageNum: `${request.pageNum}`,
            pageSize: `${request.pageSize}`,
            name: request.filter.searchFilter || '',
          },
        }),
      extraOptions: {
        maxRetries: 1,
      },
    }),
    getConversationTags: builder.query<ConversationTag[], { chatId: string }>({
      providesTags: ['tags/conversation'],
      extraOptions: { maxRetries: 1 },
      query: request =>
        apiResource(B2ChatClient.resources.tags.actions.getAll, {
          params: { chatId: request.chatId },
        }),
    }),
    assignedConversationTags: builder.query<
      ConversationTag[],
      AssignedConversationTagsRequest
    >({
      providesTags: ['tags/conversation', 'tags/conversation/assigned'],
      query: request =>
        apiResource(B2ChatClient.resources.tags.actions.getTags, {
          params: { chatId: request.chatId },
          data: {},
        }),
    }),
    assignConversationTag: builder.mutation<
      void,
      { chatId: string; tag: ConversationTag }
    >({
      invalidatesTags: ['tags/conversation/assigned'],
      query: request =>
        apiResource(B2ChatClient.resources.tags.actions.assign, {
          params: { chatId: request.chatId },
          data: request.tag,
        }),
    }),
    unassignConversationTag: builder.mutation<
      void,
      { chatId: string; tag: ConversationTag }
    >({
      invalidatesTags: ['tags/conversation/assigned'],
      query: request =>
        apiResource(B2ChatClient.resources.tags.actions.delete, {
          params: { chatId: request.chatId },
          data: request.tag,
        }),
    }),
  }),
});

export const {
  useSearchConversationTagsQuery,
  useLazySearchConversationTagsQuery,
  useAssignConversationTagMutation,
  useAssignedConversationTagsQuery,
  useLazyAssignedConversationTagsQuery,
  useGetConversationTagsQuery,
  useLazyGetConversationTagsQuery,
  useUnassignConversationTagMutation,
  useSearchContactTagsQuery,
  useLazySearchContactTagsQuery,
  util: tagsApiUtil,
  endpoints,
} = TagsApi;
